import React, { useEffect, useRef, useState } from 'react';
import { useActiveViewportDisplaySets, useSystem } from '@ohif/core';
import { Button, Tooltip, TooltipContent, TooltipTrigger, useImageViewer } from '@ohif/ui-next';
import { AccountRoleEnum } from '../../services/SessionService';
import { useOrganReview } from '../../hooks/useOrganReview';
import ButtonContent from './ButtonContent';
import OrganReviewDialog from '../OrganReviewDialog';

const BASE_CLASS =
  'flex h-10 w-10 items-center justify-center rounded-lg bg-transparent font-medium';

/**
 * ImageViewerProvider.tsx:8 creates its context with `createContext(null)`, so
 * useImageViewer()'s inferred result is `null` and every field read off it is an error.
 * The real shape is fixed at :12-14.
 */
interface ImageViewerContextValue {
  StudyInstanceUIDs: string[];
}

/**
 * develop hard-appended this component inside extensions/default/src/Toolbar/Toolbar.tsx
 * (:83-97), outside the toolbar service, and took both UIDs as props that file computed.
 * This is a registered uiType and receives neither (plan D-1):
 *
 *   - the study UID comes from useImageViewer(), because Mode.tsx:461 wraps the whole
 *     layout in ImageViewerProvider (M3 D-1). Its context defaults to null.
 *   - the series UID comes from useActiveViewportDisplaySets(), which is develop's own
 *     three-step derivation upstreamed into platform/core and self-subscribed to
 *     ACTIVE_VIEWPORT_ID_CHANGED and GRID_STATE_CHANGED
 *     (useActiveViewportDisplaySets.ts:11, :43-52). SeriesInstanceUID is optional on
 *     DisplaySet (DisplaySet.ts:18), hence the chain.
 *
 * NO `evaluate` (design D11). A custom uiType re-renders from its own hook, so it needs
 * neither an evaluator nor registerEventForToolbarUpdate — and avoiding the latter avoids
 * a leak: its unsubscribe handles land in ToolbarService._serviceSubscriptions, which is
 * push-only and never drained.
 */
export default function OrganReviewButton({ tooltip }: { tooltip?: string }) {
  const { servicesManager } = useSystem();
  const { sessionService } = servicesManager.services;

  const imageViewer = useImageViewer() as ImageViewerContextValue | null;
  const studyInstanceUid = imageViewer?.StudyInstanceUIDs?.[0];

  const displaySets = useActiveViewportDisplaySets();
  const seriesInstanceUid = displaySets?.[0]?.SeriesInstanceUID;

  /**
   * Design D4, fail CLOSED. Gating the ARGUMENTS and not only the render is the point:
   * React runs effects for a component that returns null, so passing the UIDs through
   * would have every USER-role session subscribe and issue GET organ-reviews on mount,
   * logging a 403 per study — a gate that hides the button while still calling the
   * endpoint it guards.
   */
  const isReviewer = sessionService.getRole() === AccountRoleEnum.REVIEWER;

  const {
    rows,
    hasPending,
    hasError,
    isFullyReviewed,
    isSubmitDisabled,
    setQuality,
    submit,
    refresh,
  } = useOrganReview(
    isReviewer ? studyInstanceUid : undefined,
    isReviewer ? seriesInstanceUid : undefined
  );

  const [open, setOpen] = useState(false);

  /** Where focus returns when the panel closes (plan D-14). */
  const buttonRef = useRef<HTMLButtonElement>(null);

  /**
   * Plan D-15. A close the reviewer did not ask for must not move their focus: the effect
   * below closes the panel when the active series changes, and onCloseAutoFocus cannot
   * tell that from a click on Save & Close.
   */
  const closedBySeriesChange = useRef(false);

  /**
   * Plan D-15, ruling 1. `openRef` mirrors `open` so the series-change effect below can
   * test the panel's live open state without adding `open` to its dependency array. Every
   * open/close MUST go through `setPanelOpen`, or `openRef` drifts from `open` and the
   * guard below becomes wrong in the other direction.
   */
  const openRef = useRef(false);

  const setPanelOpen = (next: boolean) => {
    openRef.current = next;
    setOpen(next);
  };

  /**
   * On ANY change of series, not only on it going falsy. useActiveViewportDisplaySets
   * re-resolves on ACTIVE_VIEWPORT_ID_CHANGED and GRID_STATE_CHANGED
   * (useActiveViewportDisplaySets.ts:43-52), so clicking another viewport in a 2x2 layout
   * swaps the panel's series underneath it — and the panel names no series, so a reviewer
   * would go on grading, against the new one, with no visual cue. Closing is the smallest
   * fix; naming the series in the title would be the other.
   *
   * Plan D-15, ruling 1: the latch is armed only when `openRef.current` is already true.
   * This effect also runs on first render and on the guaranteed undefined -> UID
   * transition (design D-1), and OrganReviewDialog's onCloseAutoFocus CONSUMES the latch
   * (resets it to false) on close — so arming it unconditionally would have the
   * reviewer's very first Save & Close find it already armed, skip the focus return, and
   * strand focus on <body>, the same failure Task 8 check 7 asserts against, reached from
   * the other side.
   */
  useEffect(() => {
    if (!openRef.current) {
      return;
    }
    closedBySeriesChange.current = true;
    setPanelOpen(false);
  }, [seriesInstanceUid]);

  /**
   * develop compared `user?.role === AccountRoleEnum.REVIEWER` against an untyped `any`
   * off useUserAuthentication (useOrganReview.ts:25-26), which is `undefined ===
   * undefined` — and so grants the button to every role — the moment the enum member goes
   * missing. Not a security boundary: enforcement is the endpoint receiving the same
   * session cookie (parent §5.5).
   *
   * Visibility, not disablement, as on develop. AFTER every hook, because a conditional
   * return before one is a hook-order error.
   */
  if (!isReviewer) {
    return null;
  }

  /**
   * Plan D-5, extended by D-15. develop disabled on isFullyReviewed
   * (OrganReviewButton.tsx:66-73), which made the panel permanently unopenable once all
   * three organs completed. hasPending is NOT in this test either, and that is load
   * bearing rather than cosmetic: a row stuck inProgress against a failing endpoint would
   * otherwise disable the button forever, hiding the very error line D-13 added to explain
   * it. Submit is still blocked while a submit is outstanding — that is isSubmitDisabled's
   * job — and ButtonContent still shows the spinner.
   */
  const isDisabled = !studyInstanceUid || !seriesInstanceUid;

  /**
   * Opening re-reads. The hook's effect runs once per series and polling stops on error,
   * so without this a failed read has no retry surface at all and §7.1 checks 8 and 9 —
   * which change the stub and then reopen — would observe nothing (plan D-15).
   */
  const openPanel = () => {
    void refresh();
    setPanelOpen(true);
  };

  return (
    <>
      {/*
        `aria-disabled`, not the native attribute, for the reason ToggleGroup.tsx:77-85
        gives: a native `<button disabled>` is unfocusable, so the tooltip explaining why
        the button is unavailable would be out of keyboard reach. The click is guarded
        below instead.
      */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            ref={buttonRef}
            dataCY="sycai-organ-review-button"
            className={`${BASE_CLASS} aria-disabled:cursor-not-allowed aria-disabled:opacity-50`}
            aria-disabled={isDisabled || undefined}
            aria-label="Organ Review"
            onClick={() => !isDisabled && openPanel()}
          >
            <ButtonContent
              hasPending={hasPending}
              isFullyReviewed={isFullyReviewed}
            />
          </Button>
        </TooltipTrigger>
        {tooltip && <TooltipContent side="bottom">{tooltip}</TooltipContent>}
      </Tooltip>

      {seriesInstanceUid && (
        <OrganReviewDialog
          open={open}
          onOpenChange={setPanelOpen}
          rows={rows}
          isSubmitDisabled={isSubmitDisabled}
          isSubmitting={hasPending}
          hasError={hasError}
          returnFocusRef={buttonRef}
          suppressFocusReturn={closedBySeriesChange}
          onQualityChange={setQuality}
          onSubmit={() => void submit()}
        />
      )}
    </>
  );
}
